import { ReactNode } from "react";
import { AdminShell } from "@/components/admin/AdminShell";
import type { AdminSection } from "@/hooks/useAdminActivityBadges";

interface ExecutivePageProps {
  section: AdminSection;
  children: ReactNode;
  /** Slot rendered at the very bottom-right for consistent MAVIS anchoring hints. */
  footerNote?: string;
  /** Hide the permanent MAVIS dock and show a floating "Ask MAVIS" button instead. */
  hideDock?: boolean;
  /** Widen the content column. Default matches the rest of admin (max-w-7xl). */
  maxWidthClassName?: string;
}

/**
 * The Executive Office page shell.
 *
 * Consistent rhythm on every admin page:
 *   - Container width (`max-w-7xl`)
 *   - Generous vertical breathing room (`space-y-6`)
 *   - Warm paper background inherited from theme
 *
 * Never wrap content in a card here — the page IS the paper.
 */
export function ExecutivePage({
  section,
  children,
  footerNote,
  hideDock,
  maxWidthClassName = "max-w-7xl",
}: ExecutivePageProps) {
  return (
    <AdminShell section={section} hideDock={hideDock}>
      <div className={`container mx-auto ${maxWidthClassName} px-4 md:px-6 pt-6 pb-16 space-y-6`}>
        {children}
        {footerNote && (
          <footer className="pt-8 text-center text-xs text-muted-foreground">
            {footerNote}
          </footer>
        )}
      </div>
    </AdminShell>
  );
}
